import React from 'react';
import { FaGithub, FaNpm, FaInfoCircle } from 'react-icons/fa';
import { SiPypi } from 'react-icons/si';
import { input } from '../../utils/designTokens';

/**
 * Step 7: Repository Links
 * - GitHub URL, npm package, pip package
 * - URL validation
 */
const MCPServerFormStep7 = ({ formData, updateFormData }) => {
  const handleChange = (e) => {
    const { name, value } = e.target;
    updateFormData({ [name]: value });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-spooky-ghost-white">
          Repository & Package Links
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Help users find your source code and installation packages (all optional).
        </p>
      </div>

      {/* GitHub URL */}
      <div>
        <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300" htmlFor="githubUrl">
          <FaGithub className="inline mr-2" />
          GitHub Repository URL
        </label>
        <input
          type="url"
          id="githubUrl"
          name="githubUrl"
          value={formData.githubUrl}
          onChange={handleChange}
          placeholder="https://github.com/username/repository"
          className={input}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Link to your GitHub repository for source code access
        </p>
      </div>

      {/* npm Package */}
      <div>
        <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300" htmlFor="npmPackage">
          <FaNpm className="inline mr-2 text-red-600" />
          npm Package Name
        </label>
        <div className="flex items-center gap-2">
          <span className="text-gray-500 dark:text-gray-400 font-mono text-sm">npm install</span>
          <input
            type="text"
            id="npmPackage"
            name="npmPackage"
            value={formData.npmPackage}
            onChange={handleChange}
            placeholder="package-name"
            className={`flex-1 ${input}`}
          />
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Package name for installation via npm (Node.js)
        </p>
      </div>

      {/* pip Package */}
      <div>
        <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300" htmlFor="pipPackage">
          <SiPypi className="inline mr-2 text-blue-600" />
          PyPI Package Name
        </label>
        <div className="flex items-center gap-2">
          <span className="text-gray-500 dark:text-gray-400 font-mono text-sm">pip install</span>
          <input
            type="text"
            id="pipPackage"
            name="pipPackage"
            value={formData.pipPackage}
            onChange={handleChange}
            placeholder="package-name"
            className={`flex-1 ${input}`}
          />
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Package name for installation via pip (Python)
        </p>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 dark:text-blue-300 mb-2 flex items-center gap-2">
          <FaInfoCircle />
          Why add repository links?
        </h3>
        <ul className="text-sm text-blue-800 dark:text-blue-400 space-y-1">
          <li>• Increases trust by showing source code</li>
          <li>• Makes installation easier with package managers</li>
          <li>• Helps users report issues and contribute</li>
          <li>• Improves discoverability through package registries</li>
        </ul>
      </div>

      {/* Preview Links */}
      {(formData.githubUrl || formData.npmPackage || formData.pipPackage) && (
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 dark:text-gray-300 mb-3">
            Link Preview
          </h3>
          <div className="space-y-2">
            {formData.githubUrl && (
              <a
                href={formData.githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-sm text-cyan-600 dark:text-cyan-400 hover:underline"
              >
                <FaGithub className="inline mr-2" />
                {formData.githubUrl}
              </a>
            )}
            {formData.npmPackage && (
              <a
                href={`https://www.npmjs.com/package/${formData.npmPackage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-sm text-cyan-600 dark:text-cyan-400 hover:underline"
              >
                <FaNpm className="inline mr-2" />
                npmjs.com/package/{formData.npmPackage}
              </a>
            )}
            {formData.pipPackage && (
              <a
                href={`https://pypi.org/project/${formData.pipPackage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-sm text-cyan-600 dark:text-cyan-400 hover:underline"
              >
                <SiPypi className="inline mr-2" />
                pypi.org/project/{formData.pipPackage}
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MCPServerFormStep7;
